import React, { useEffect, useState } from 'react'
import DashboardStyle from '../Dashboard'
import { customFuncsApi } from '../../../api'
import { Empty, Input } from 'antd'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import ClipLoader from 'react-spinners/ClipLoader'
import Button from '../../../components/Button'
import { formatNumber } from '../../../utils/formatNumber'
import { debouncedSearch } from '../../../utils/debounce'

export default function Tab4() {
	const { t } = useTranslation()

	const [isLoading, setIsLoading] = useState(false)

	const [startDate, setStartDate] = useState(
		moment().startOf('month').format('YYYY-MM-DD'),
	)
	const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'))
	const [customerName, setCustomerName] = useState('')
	const [serialNumber, setSerialNumber] = useState('')
	const [customerNameInput, setCustomerNameInput] = useState('')
	const [serialNumberInput, setSerialNumberInput] = useState('')
	const [invoiceData, setInvoiceData] = useState([])
	const [invoicePage, setInvoicePage] = useState(0)
	// Server-side sort. orderBy === null → default order
	// («Дата продажи» newest-first). Otherwise one of the API sort
	// keys: 'overdueMonth' («Просрочка за месяц») |
	// 'monthsOverdue' («Просрочено месяцев»).
	const [orderBy, setOrderBy] = useState(null)
	const [orderType, setOrderType] = useState('DESC')
	const [totalCalculation, setTotalCalculation] = useState({
		docTotal: 0,
		monthlyPayment: 0,
		paidForMonth: 0,
		overpayMonth: 0,
		collectedWithoutOverpay: 0,
		paidPercent: 0,
	})

	const getInvoiceReports = (
		startDate,
		endDate,
		customerName,
		serialNumber,
		currentPage,
		orderBy,
		orderType,
	) => {
		setIsLoading(true)
		customFuncsApi
			.get(`xsSql/getInvoiceReportV2`, {
				params: {
					startDate: startDate + ' 00:00:00.000Z',
					endDate: endDate + ' 00:00:00.000Z',
					customerName: customerName || undefined,
					serialNumber: serialNumber || undefined,
					$skip: currentPage * 20,
					// Both omitted when orderBy is null → server keeps
					// the default «Дата продажи» newest-first ordering.
					orderBy: orderBy || undefined,
					orderType: orderBy ? orderType : undefined,
				},
				headers: {
					Prefer: 'odata.maxpagesize=20',
				},
			})
			.then((res) => {
				const resData = res.data.value || []

				// The API appends a server-computed grand-total row
				// (Имя клиента === 'ИТОГО:', DocNum === null) as the last
				// element. Pull it out so it isn't rendered inside the
				// scrollable table, and surface it in the fixed summary bar.
				const totalsRow = resData.find(
					(r) => r['Имя клиента'] === 'ИТОГО:',
				)
				const rows = resData.filter(
					(r) => r['Имя клиента'] !== 'ИТОГО:',
				)
				setInvoiceData(rows)

				if (totalsRow) {
					const monthlyPayment = Number(
						totalsRow['Ежемесячная оплата'] || 0,
					)
					const paidForMonth = Number(
						totalsRow['Оплачено за месяц'] || 0,
					)
					const overpayMonth = Number(
						totalsRow['Переплата за месяц'] || 0,
					)
					const collectedWithoutOverpay = paidForMonth - overpayMonth
					const paidPercent = monthlyPayment
						? (
								((paidForMonth - overpayMonth) * 100) /
								monthlyPayment
							).toFixed(2)
						: 0

					setTotalCalculation({
						docTotal: Number(totalsRow['Сумма документа'] || 0),
						monthlyPayment,
						paidForMonth,
						overpayMonth,
						collectedWithoutOverpay,
						paidPercent,
					})
				} else {
					setTotalCalculation({
						docTotal: 0,
						monthlyPayment: 0,
						paidForMonth: 0,
						overpayMonth: 0,
						collectedWithoutOverpay: 0,
						paidPercent: 0,
					})
				}
			})
			.finally(() => {
				setIsLoading(false)
			})
	}

	useEffect(() => {
		setInvoicePage(0)
	}, [startDate, endDate, customerName, serialNumber, orderBy, orderType])

	useEffect(() => {
		getInvoiceReports(
			startDate,
			endDate,
			customerName,
			serialNumber,
			invoicePage,
			orderBy,
			orderType,
		)
	}, [
		startDate,
		endDate,
		customerName,
		serialNumber,
		invoicePage,
		orderBy,
		orderType,
	])

	// Per-column cycle, one active column at a time:
	// other column / none → DESC → ASC → back to default order.
	const toggleSort = (key) => {
		if (orderBy !== key) {
			setOrderBy(key)
			setOrderType('DESC')
		} else if (orderType === 'DESC') {
			setOrderType('ASC')
		} else {
			setOrderBy(null)
			setOrderType('DESC')
		}
	}

	const onCustomerNameChange = (e) => {
		const v = e.target.value
		setCustomerNameInput(v)
		debouncedSearch(v, setCustomerName)
	}

	const onSerialNumberChange = (e) => {
		const v = e.target.value
		setSerialNumberInput(v)
		debouncedSearch(v, setSerialNumber)
	}

	const newDatas = () => {
		if (invoiceData.length < 20) {
			alert(t('noOtherInfo'))
		} else {
			setInvoicePage((prev) => prev + 1)
		}
	}

	const oldData = () => {
		if (invoicePage > 0) {
			setInvoicePage((prev) => prev - 1)
		} else {
			alert(t('noOtherInfo'))
		}
	}

	// Updated columns with new field names
	const columns = [
		{
			title: t('docNum'),
			dataIndex: 'DocNum',
			key: 'DocNum',
		},
		{
			title: 'Дата продажи',
			dataIndex: 'Дата продажи',
			key: 'docDate',
			render: (value) => (value ? moment(value).format('YYYY-MM-DD') : ''),
		},
		{
			title: t('clientName'),
			dataIndex: 'Имя клиента',
			key: 'clientName',
		},
		{
			title: t('phoneNumber'),
			dataIndex: 'U_Telephone',
			key: 'phone',
		},
		{
			title: 'Марка авто',
			dataIndex: 'Марка авто',
			key: 'carBrand',
		},
		{
			title: t('serialNum'),
			dataIndex: 'Серийный номер',
			key: 'serialNumber',
		},
		{
			title: 'Номер авто',
			dataIndex: 'Номер авто',
			key: 'carNumber',
		},
		{
			title: t('docTotal'),
			dataIndex: 'Сумма документа',
			key: 'docTotal',
			render: (value) => formatNumber(value),
		},
		{
			title: t('installmentMonth'),
			dataIndex: 'Месяц рассрочки',
			key: 'installmentMonth',
		},
		{
			title: 'Дата оплаты',
			dataIndex: 'Дата оплаты',
			key: 'paymentDate',
			render: (value) => (value ? moment(value).format('YYYY-MM-DD') : ''),
		},
		{
			title: 'Ежемесячная оплата',
			dataIndex: 'Ежемесячная оплата',
			key: 'monthlyInstallment',
			render: (value) => formatNumber(value),
		},
		{
			title: 'Оплачено за месяц',
			dataIndex: 'Оплачено за месяц',
			key: 'paidForMonth',
			render: (value) => formatNumber(value),
		},
		{
			title: 'Переплата всего',
			dataIndex: 'Переплата всего',
			key: 'overpaymentTotal',
			render: (value) => formatNumber(value),
		},
		{
			title: 'Просрочка всего',
			dataIndex: 'Просрочка всего',
			key: 'overdueTotal',
			render: (value) => formatNumber(value),
		},

		{
			title: 'Переплата за месяц',
			dataIndex: 'Переплата за месяц',
			key: 'overpayMonth',
			render: (value) => formatNumber(value),
		},
		{
			title: 'Просрочка за месяц',
			dataIndex: 'Просрочка за месяц',
			key: 'overdueMonth',
			sortKey: 'overdueMonth',
			render: (value) => formatNumber(value),
		},
		{
			title: 'Просрочено месяцев',
			dataIndex: 'Просрочено месяцев',
			key: 'monthOverdue',
			sortKey: 'monthsOverdue',
			render: (value) =>
				Intl.NumberFormat('fr-FR', {
					minimumFractionDigits: 0,
					maximumFractionDigits: 2,
				}).format(value),
		},
		{
			title: 'Кумулятивно оплачено (всего)',
			dataIndex: 'Кумулятивно оплачено (всего)',
			key: 'cumulativePaid',
			render: (value) => formatNumber(value),
		},
		{
			title: 'Кумулятивно оплачено (%) (всего)',
			dataIndex: 'Кумулятивно оплачено (%) (всего)',
			key: 'cumulativePaidPercent',
			render: (value) => `${formatNumber(value)} %`,
		},
		{
			title: 'Кумулятивно оплачено (без 1-го платежа)',
			dataIndex: 'Кумулятивно оплачено (без 1-го платежа)',
			key: 'cumulativeWithoutFirst',
			render: (value) => formatNumber(value),
		},
		{
			title: 'Кумулятивно оплачено (%) (без 1-го платежа)',
			dataIndex: 'Кумулятивно оплачено (%) (без 1-го платежа)',
			key: 'cumulativeWithoutFirstPercent',
			render: (value) => `${formatNumber(value)} %`,
		},
		{
			title: 'Скидка за период',
			dataIndex: 'Скидка за период',
			key: 'discountPeriod',
			render: (value) => formatNumber(value),
		},
		{
			title: 'Скидка до периода',
			dataIndex: 'Скидка до периода',
			key: 'discountBefore',
			render: (value) => formatNumber(value),
		},
		{
			title: 'Скидка всего (до конца)',
			dataIndex: 'Скидка всего (до конца)',
			key: 'discountTotal',
			render: (value) => formatNumber(value),
		},
		{
			title: t('balanceSaldo'),
			dataIndex: 'Остаток сальдо',
			key: 'balanceSaldo',
			render: (value) => formatNumber(value),
		},
	]

	return (
		<DashboardStyle>
			<div className="container minWidthContainer">
				<div>
					<h4 className={'font-bold text-lg text-center mt-10 mb-4'}>
						{t('invoiceReports')}
					</h4>

					<div className="mb-4 flex items-end gap-10 justify-end flex-wrap">
						<div className="flex flex-col gap-2">
							<span>{t('clientName')}</span>
							<Input
								allowClear
								placeholder={t('clientName')}
								value={customerNameInput}
								onChange={onCustomerNameChange}
							/>
						</div>
						<div className="flex flex-col gap-2">
							<span>{t('serialNum')}</span>
							<Input
								allowClear
								placeholder={t('serialNum')}
								value={serialNumberInput}
								onChange={onSerialNumberChange}
							/>
						</div>
						<div className="flex flex-col gap-2">
							<span>{t('startDate')}</span>
							<Input
								type="date"
								value={startDate}
								onChange={(e) => setStartDate(e.target.value)}
							/>
						</div>
						<div className="flex flex-col gap-2">
							<span>{t('endDate')}</span>
							<Input
								type="date"
								value={endDate}
								onChange={(e) => setEndDate(e.target.value)}
							/>
						</div>
					</div>

					{isLoading ? (
						<div style={{ display: 'flex', justifyContent: 'center' }}>
							<ClipLoader
								loading={true}
								size={20}
								aria-label="Loading Spinner"
								className={'loader'}
								data-testid="loader"
							/>
						</div>
					) : (
						<>
						<div className="overflow-auto mb-8 max-h-[70vh] relative">
							<table className="w-full text-sm text-left rtl:text-right text-gray-500">
								<thead className="text-xs text-gray-700 uppercase bg-gray-50 sticky top-0 z-10">
									<tr>
										{columns.map((col) => (
											<th
												key={col.key}
												scope="col"
												onClick={
													col.sortKey
														? () =>
																toggleSort(
																	col.sortKey,
																)
														: undefined
												}
												title={
													col.sortKey
														? t('clickToSort')
														: undefined
												}
												className={`px-6 py-4 bg-gray-50 whitespace-nowrap ${
													col.sortKey
														? 'cursor-pointer select-none hover:bg-gray-100'
														: ''
												}`}
											>
												{col.title}
												{col.sortKey && (
													<span className="ml-1 inline-block">
														{orderBy !==
														col.sortKey
															? '↕'
															: orderType ===
																  'ASC'
																? '▲'
																: '▼'}
													</span>
												)}
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{invoiceData.length > 0 ? (
										invoiceData.map((row, i) => (
											<tr
												key={row.DocNum ?? i}
												className="bg-white border-b hover:bg-gray-50"
											>
												{columns.map((col) => (
													<td
														key={col.key}
														className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap"
													>
														{col.render
															? col.render(row[col.dataIndex], row, i)
															: row[col.dataIndex]}
													</td>
												))}
											</tr>
										))
									) : (
										<tr>
											<td colSpan={columns.length}>
												<Empty
													description={t('noInfo')}
													image={Empty.PRESENTED_IMAGE_SIMPLE}
												/>
											</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>

						<div className="mb-8 flex flex-wrap items-stretch gap-4">
							<div className="flex-1 min-w-[180px] rounded-lg border bg-gray-50 px-5 py-3">
								<div className="text-xs text-gray-500 uppercase">
									Сумма документа (итого)
								</div>
								<div className="text-base font-semibold text-gray-900">
									{formatNumber(totalCalculation.docTotal)}
								</div>
							</div>
							<div className="flex-1 min-w-[180px] rounded-lg border bg-gray-50 px-5 py-3">
								<div className="text-xs text-gray-500 uppercase">
									Ежемесячная оплата (итого)
								</div>
								<div className="text-base font-semibold text-gray-900">
									{formatNumber(totalCalculation.monthlyPayment)}
								</div>
							</div>
							<div className="flex-1 min-w-[180px] rounded-lg border bg-gray-50 px-5 py-3">
								<div className="text-xs text-gray-500 uppercase">
									Оплачено за месяц (итого)
								</div>
								<div className="text-base font-semibold text-gray-900">
									{formatNumber(totalCalculation.paidForMonth)}
								</div>
							</div>
							<div className="flex-1 min-w-[180px] rounded-lg border bg-gray-50 px-5 py-3">
								<div className="text-xs text-gray-500 uppercase">
									Переплата за месяц (итого)
								</div>
								<div className="text-base font-semibold text-gray-900">
									{formatNumber(totalCalculation.overpayMonth)}
								</div>
							</div>
							<div className="flex-1 min-w-[180px] rounded-lg border bg-gray-50 px-5 py-3">
								<div className="text-xs text-gray-500 uppercase">
									Оплачено без переплаты (итого)
								</div>
								<div className="text-base font-semibold text-gray-900">
									{formatNumber(
										totalCalculation.collectedWithoutOverpay,
									)}
								</div>
							</div>
							<div className="flex-1 min-w-[180px] rounded-lg border border-blue-200 bg-blue-50 px-5 py-3">
								<div className="text-xs text-blue-700 uppercase">
									Процент оплаты
								</div>
								<div className="text-lg font-bold text-blue-900">
									{totalCalculation.paidPercent}%
								</div>
							</div>
						</div>
						</>
					)}

					<div className="flex items-center gap-2 mt-4">
						<Button className={'btn'} onClick={oldData}>
							{'<'}
						</Button>
						<span>{invoicePage + 1}</span>
						<Button className={'btn'} onClick={newDatas}>
							{'>'}
						</Button>
					</div>
				</div>
			</div>
		</DashboardStyle>
	)
}
